import { useEffect, useState } from "react";
import { IMonthMap, IMonthMapElement } from "../MonthMapEditor/MonthMapEditor";
import "./CustomDatePicker.scss";
import { IBook } from "../../Models/IBook";
import { useSelector } from "react-redux";
import { GlobalState } from "../../Redux/RootReducer";
import BetterDropDown from "../BetterDropDown/BetterDropDown";
import Tr from "../../Utils/Translations/Translations";
import BetterTextField from "../BetterTextField/BetterTextField";
import { IBookTimeline } from "../../Models/IBookTimeline";
import { mobileCheck } from "../../App";

export interface ICustomDatePickerProps {
  onRenderLabel?: (el: any) => JSX.Element;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  currentValue?: string;
  onChange: (date: string) => void;
  style?: any;
}


export const MonthIndexJSON = (map: string | undefined, monthName: string) => {
  if (map) {
    return MonthIndex(JSON.parse(map), monthName);
  }
  return -1;
};

export const MonthIndex = (map: IMonthMap, monthName: string) => {
  let index = -1;
  for (let i = 0; i < map.Months.length; i++) {
    if (map.Months[i].MonthName === monthName) {
      return i;
    }
  }
  return index;
};

export const CustomDateDaysToText = (map: IMonthMap, totalDays: number) => {
  let years: number = 1;
  let months: number = 0;
  let days: number = 0;

  let monthCursorIndex: number = 0;

  while (true) {
    months = monthCursorIndex % map.Months.length;
    let monthDays = map.Months[months].MonthMaxDays;
    let monthLeap = map.Months[months].LeapYearEveryTotYears;
    if (years % monthLeap === 0 && monthLeap != 0) {
      monthDays += 1;
    }

    // you can subtract this mont
    if (totalDays >= monthDays) {
      totalDays -= monthDays;
    }
    // there are less days remaining than the current month, so add them to the
    // count and leave
    else {
      days = totalDays;
      break;
    }

    // you reached last month, increase year count
    if (months === map.Months.length - 1) {
      years++;
    }

    // step next month
    monthCursorIndex += 1;
  }

  return (
    years -
    1 +
    " " +
    Tr.Translate("language", "years") +
    ", " +
    months +
    " " +
    Tr.Translate("language", "months") +
    ", " +
    days +
    " " +
    Tr.Translate("language", "days")
  );
};

export const CustomDateDaysToTextJSON = (
  map: string | undefined,
  totalDays: number
) => {
  if (map) {
    return CustomDateDaysToText(JSON.parse(map), totalDays);
  }
  return "";
};

const CustomDatePicker = (props: ICustomDatePickerProps) => {
  const [year, setYear] = useState<number>(1);
  const [month, setMonth] = useState<string>("");
  const [day, setDay] = useState<number>(1);
  const [monthMap, setMonthMap] = useState<IMonthMap>();
  const [maxDaysForMonth, setMaxDaysForMonth] = useState<number>(1);

  const activeBook: IBook | undefined = useSelector(
    (state: GlobalState) => state.generic.activeBook
  );
  const activeTimeline: IBookTimeline | undefined = useSelector(
    (state: GlobalState) => state.generic.activeTimeline
  );

  /**
   * Calculates the maximum allowed days for the current month/year selection
   * factoring in fantasy leap year rules.
   */
  const setMaxDays = () => {
    if (monthMap) {
      const targetBaseMonth: IMonthMapElement | undefined = monthMap.Months.find(
        (x: IMonthMapElement) => x.MonthName === month
      );

      if (targetBaseMonth) {
        let calculatedMax = targetBaseMonth.MonthMaxDays;

        // Leap Year Logic: Check if the year is divisible by the month's leap rule
        if (
          targetBaseMonth.LeapYearEveryTotYears !== 0 &&
          year % targetBaseMonth.LeapYearEveryTotYears === 0
        ) {
          calculatedMax += 1;
        }

        setMaxDaysForMonth(calculatedMax);

        // Safety: If the user changes the year/month and the current day 
        // is now out of bounds (e.g., Feb 29 to Feb 28), clamp it.
        if (day > calculatedMax) {
          setDay(calculatedMax);
        }
      }
    }
  };

  // Initial Load & Parsing
  useEffect(() => {
    if (activeBook?.MonthMap) {
      const parsedMap: IMonthMap = JSON.parse(activeBook.MonthMap);
      setMonthMap(parsedMap);

      if (props.currentValue) {
        const splDate: string[] = props.currentValue.split("/");
        setYear(parseInt(splDate[0], 10) || 1);
        setMonth(splDate[1]);
        setDay(parseInt(splDate[2], 10) || 1);
      } else if (parsedMap.Months.length > 0) {
        setMonth(parsedMap.Months[0].MonthName);
      }
    }
  }, [activeBook, props.currentValue]);

  // Recalculate max days whenever the month or year changes
  useEffect(() => {
    setMaxDays();
  }, [month, year, monthMap]);

  // Notify parent of changes
  useEffect(() => {
    props.onChange(`${year}/${month}/${day}`);
  }, [year, month, day]);

  const inputWidth = mobileCheck() ? "100%" : "33%";

  return (
    <div style={props.style}>
      {monthMap && (
        <div
          className="custom-date-picker-main-wrap"
          style={mobileCheck() ? { flexDirection: "column" } : {}}
        >
          {/* YEAR INPUT */}
          <div style={{ width: inputWidth }}>
            <BetterTextField
              label={Tr.Translate("language", "year")}
              required={props.required}
              disabled={props.disabled}
              min={1}
              max={activeTimeline?.TimelineYearSpan}
              type="number"
              value={year.toString()}
              onChange={(e, o) => setYear(o ? parseInt(o, 10) : 1)}
            />
          </div>

          {/* MONTH INPUT */}
          <div style={{ width: inputWidth }}>
            <BetterDropDown
              label={Tr.Translate("language", "month")}
              required={props.required}
              disabled={props.disabled}
              currentValue={month}
              onChange={(e, o) => {
                if (o) setMonth(o.key.toString());
              }}
              options={(monthMap?.Months ?? []).map((x: IMonthMapElement) => ({
                key: x.MonthName,
                text: x.MonthName,
              }))}
            />
          </div>

          {/* DAY INPUT */}
          <div style={{ width: inputWidth }}>
            <BetterTextField
              label={Tr.Translate("language", "day")}
              required={props.required}
              disabled={props.disabled}
              type="number"
              min={1}
              max={maxDaysForMonth} // Dynamically updated via setMaxDays
              value={day.toString()}
              onChange={(e, o) => setDay(o ? parseInt(o, 10) : 1)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDatePicker;